// /kit — every piece of the chrome with sample data, so the look is checked before screens are wired to the game.
import { useState } from "react";
import { BottomNav, Shell, TitleBar, TitleHeader, WorldHeader } from "@/kit/chrome";
import { art } from "@/kit/art";
import { IconHourglass, IconScroll, IconShield } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Bar, Button, Card, CardHead, Disc, Field, HeroCard, LinkButton, Medallion, Row, RowText, Seg, Tabs } from "@/kit/primitives";
import { usePrompts } from "@/state/prompts";
import { IconGear, IconBell } from "@/kit/icons";
import { AmountSlider, CostLine, LevelDisc, LevelTrack, NowNext, Pips, ResMedal, ResourceBar, Stepper, TierDisc, Toggle, UnitMedal } from "@/kit/city";
import type { Village } from "@/api/types";

const SAMPLE_CITY = { resources: { r_w: 54800, r_c: 114000, r_i: 129000 }, resources_production: { r_w: 1410, r_c: 1200, r_i: 980 }, resources_capacity: 404240, max_population: 17318, current_population: 11186, active_effects: { boost_wood: { effect_key: "boost_wood", expires_at: Date.now() + 1e8 } } } as unknown as Village;

export function KitPage() {
  const [hdr, setHdr] = useState<"title" | "city">("title");
  const [seg, setSeg] = useState<"army" | "economy" | "storage" | "defense">("army");
  const [tab, setTab] = useState<"all" | "unread" | "sent">("all");
  const [sheet, setSheet] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [amount, setAmount] = useState(40);
  const [step, setStep] = useState(1);
  const [on, setOn] = useState(true);
  const prompts = usePrompts();

  const common = { worldLabel: "World 2", notifications: 3, onBell: () => prompts.toast("Bell"), onMenu: () => prompts.toast("Menu"), onWorld: () => setHdr(hdr === "title" ? "city" : "title") };
  const header = hdr === "title"
    ? <TitleHeader {...common} title="PORTAL" subtitle="Account overview" avatarLevel="45" />
    : <WorldHeader {...common} city="Quark - 001" meta={<>(505, 518) · <b>563</b> pts · 3 cities</>} v={SAMPLE_CITY} flags={{ reports: true, mail: true, news: true }}
        onCity={() => prompts.toast("City switcher")} onReports={() => prompts.toast("Reports")} onMail={() => prompts.toast("Mail")} />;

  return (
    <Shell className={hdr === "city" ? "wf" : undefined} header={header} nav={<BottomNav wid={2} badges={{ quests: 2, map: 1 }} />}>
      <div className="stack">
        <TitleBar title="Design kit" onBack={() => prompts.toast("Back")} action={{ label: "Settings", onClick: () => prompts.toast("Gear") }} />
        <Card small>
          <CardHead title="Design kit" aside="tap the world tab to switch header" />
          <p className="hint" style={{ margin: 0 }}>Slate & Fire — natural charcoal stone, antique gold edges and soft warmth from the selected control. Tap the world selector to preview both headers.</p>
        </Card>

        <HeroCard
          art={art.hero.portal}
          title="Your legend"
          lines={[<>Lord of <b className="gold">4 worlds</b> since March 2025.</>, <>Best finish: rank <b className="gold">#12</b> on <b className="gold">World 2</b>.</>]}
          link={<LinkButton onClick={() => prompts.toast("View career")}>View career</LinkButton>}
          stats={[{ label: "Worlds won", value: "1" }, { label: "Worlds played", value: "6" }, { label: "Troops slain", value: "412K" }, { label: "Titles", value: "7" }]}
        />

        <HeroCard
          art={art.hero.home}
          artWidth="68%"
          full
          title="Rank #19"
          lines={[<><b className="gold">2 cities</b> · guild <b className="gold">[VALR]</b> Valor Reforged</>]}
          stats={[{ label: "Points", value: "36,723" }, { label: "Kill score", value: "158" }, { label: "As attacker", value: "45" }, { label: "As defender", value: "113" }]}
        />

        <Card>
          <CardHead title="Worlds needing attention" aside="3 of 4 worlds" asideCaps />
          <div className="rows">
            <Row alert>
              <Medallion src={art.icon.shield} alt="" glow="crim" />
              <RowText eyebrow="World 1" line={<><span className="r">Under attack</span> · 3 incoming</>} meta="First lands in 04:12" />
              <div className="right"><Button kind="crimson" size="md" badge={3}>Defend</Button></div>
            </Row>
            <Row>
              <Medallion><IconHourglass /></Medallion>
              <RowText eyebrow="World 2" line={<>Build queue empty in <b>2 cities</b></>} meta="Idle for 1h 20m" />
              <div className="right"><Button size="md">Go to</Button></div>
            </Row>
            <Row>
              <Medallion src={art.icon.swords} alt="" />
              <RowText eyebrow="World 4" line={<>Troops home · <b>6</b> unread reports</>} meta="Returned 15m ago" />
              <div className="right"><Button size="md">Go to</Button></div>
            </Row>
          </div>
          <div className="card-foot"><span><span className="dot" /><b className="gold">World 3</b> — all quiet</span><LinkButton>All worlds</LinkButton></div>
        </Card>

        <Card>
          <CardHead title="Unclaimed rewards" badge={5}><LinkButton>View all quests</LinkButton></CardHead>
          <div className="rows">
            <Row>
              <Medallion src={art.hud.wood} alt="" />
              <RowText eyebrow="World 1 · Daily" line="Gather Resources" meta={<span style={{ display: "flex", alignItems: "center", gap: 10 }}><Bar value={1} color="green" /><span className="greentxt">1.2M / 1.2M</span></span>} />
              <div className="right"><Button kind="gold" size="md">Claim</Button></div>
            </Row>
            <Row>
              <Medallion src={art.nav.quests} alt="" />
              <RowText eyebrow="World 2 · Daily" line="Win 3 Battles" meta={<span style={{ display: "flex", alignItems: "center", gap: 10 }}><Bar value={1} color="green" /><span className="greentxt">3 / 3</span></span>} />
              <div className="right"><Button kind="gold" size="md">Claim</Button></div>
            </Row>
          </div>
          <div className="card-foot"><span>+ 3 more on <b className="gold">World 3</b> and <b className="gold">World 4</b></span><LinkButton caps chevron={false}>Claim all</LinkButton></div>
        </Card>

        <Card>
          <CardHead title="Controls" />
          <div className="stack" style={{ gap: 16 }}>
            <Seg value={seg} onChange={setSeg} options={[{ value: "army", label: "Army" }, { value: "economy", label: "Economy", badge: 4 }, { value: "storage", label: "Storage" }, { value: "defense", label: "Defense" }]} />
            <Tabs value={tab} onChange={setTab} options={[{ value: "all", label: "All" }, { value: "unread", label: "Unread" }, { value: "sent", label: "Sent" }]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button kind="crimson">Attack</Button>
              <Button>Support</Button>
              <Button kind="gold">Speed up</Button>
              <Button kind="ghost">Ghost</Button>
              <Button disabled>Disabled</Button>
              <Button size="sm">Go to</Button>
              <Button kind="crimson" busy>Sending</Button>
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <Disc label="Settings"><IconGear /></Disc>
              <Disc label="Bell" badge={3}><IconBell /></Disc>
              <Disc size="sm" label="Small"><IconScroll /></Disc>
              <Disc size="xs" label="Tiny"><IconShield /></Disc>
              <Medallion initial="T" size={54} />
              <Medallion src={art.avatar} alt="" glow size={72} />
              <Medallion src={art.nav.quests} alt="" dim size={54} />
            </div>
            <div className="stack" style={{ gap: 8 }}>
              <Bar value={0.38} diamond thick />
              <Bar value={0.72} color="green" />
              <Bar value={0.2} color="crim" />
            </div>
            <Field label="Email" placeholder="you@example.com" hint="Your Valor account's email." />
            <Field label="Password" type="password" error="At least 8 characters." />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button onClick={() => setSheet(true)}>Open sheet</Button>
              <Button kind="crimson" onClick={() => setConfirm(true)}>Confirm sheet</Button>
              <Button onClick={() => prompts.toast("A toast, like the game's prompt_toast.")}>Toast</Button>
              <Button onClick={() => prompts.toast("Something went wrong.", "error")}>Error toast</Button>
              <Button onClick={() => prompts.dialogShow("Update required!", "This version of Valor is too old. Please update your app")}>Dialog</Button>
            </div>
          </div>
        </Card>

        <Card small>
          <CardHead title="Quests" badge={2} aside="2 to claim · resets 18h 42m" onToggle={() => prompts.toast("toggle")} open={false} />
        </Card>

        <Card>
          <CardHead title="City kit" aside="Phase 1 pieces" />
          <ResourceBar v={SAMPLE_CITY} />
          <NowNext nowLabel="Now · Level 29" nextLabel="Next · Level 30" now="404,240" next="500,000" delta="+95,760 (+24%)" />
          <LevelTrack level={29} max={30} />
          <div style={{ marginTop: "calc(var(--u) * 16)" }}><CostLine cost={{ r_w: 60, r_c: 30, r_i: 20 }} pop={1} time={135000} short={{ r_i: 5 }} /></div>
          <AmountSlider value={amount} max={916} onChange={setAmount} label="Lancers" />
          <div style={{ display: "flex", gap: "calc(var(--u) * 16)", alignItems: "center", flexWrap: "wrap", marginTop: "calc(var(--u) * 18)" }}>
            <Stepper value={step} min={1} max={9} onChange={setStep} label="Copies" />
            <Toggle on={on} onChange={setOn} label="Sample toggle" />
            <LevelDisc level={18} /> <TierDisc tier={2} /> <Pips level={2} /> <Pips level={2} boxes />
          </div>
          <div style={{ display: "flex", gap: "calc(var(--u) * 14)", alignItems: "center", flexWrap: "wrap", marginTop: "calc(var(--u) * 18)" }}>
            <UnitMedal troop="t_lc" tier={3} size={64} count={2016} />
            <UnitMedal troop="t_sw" tier={1} size={64} locked dim />
            <UnitMedal troop="t_n" tier={1} size={64} />
            <ResMedal r="r_w" size={64} selected onClick={() => prompts.toast("wood")} />
            <ResMedal r="r_c" size={64} onClick={() => prompts.toast("clay")} />
            <ResMedal r="any" size={64} onClick={() => prompts.toast("any")} />
          </div>
        </Card>
      </div>
      <Sheet open={sheet} onClose={() => setSheet(false)} label="Sample sheet" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>A bottom sheet</h3>}>
        <p className="hint" style={{ fontSize: "var(--fs-body)", color: "var(--cream)" }}>Sheets share the slate finish and gold framing of the main screens.</p>
        <Button kind="crimson" wide onClick={() => setSheet(false)}>Close</Button>
      </Sheet>
      <Confirm open={confirm} onClose={() => setConfirm(false)} title="Send attack" text={"2,016 Knights, 723 Guardians and 1 Scholar march on Thrunggrok (506, 517).\nTravel 00:35:21 · lands 20:59:04."} action="Attack" onConfirm={() => { setConfirm(false); prompts.toast("Sent (not really).", "success"); }} />
    </Shell>
  );
}
